'use strict';

import React, { memo, useMemo } from 'react';

import Paper from '@mui/material/Paper';
import { withStyles } from '@mui/styles';
import { grey } from '@mui/material/colors';
import { useLangFile } from 'js/context/LanguageContext';
import { useMultipleJobs, usePrescriptionJob } from './PrescriptionJobContext';
import { PRESCRIPTION_UNIT } from 'js/constants/PrescriptionConstants';
import { connect } from 'react-redux';
import { getFieldName } from '../../helpers/StateInterpreters';
import { usePrescriptionJobOrchestration } from './SeedingJob/usePrescriptionJobOrchestration';
import { PrescriptionState } from './PrescriptionState';

const styles = (theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'flex-start',
    padding: 8,
    borderBottom: `1px solid ${grey[200]}`,
  },
  items: {
    display: 'flex',
    flexDirection: 'row',
  },
  item: {
    display: 'flex',
    justifyContent: 'center',
    justifyItems: 'center',
    flex: 1,
    alignItems: 'center',
  },
  label: {
    fontSize: '16px',
    padding: [[0, 8]],
    whiteSpace: 'nowrap',
  },
});

const mapStateToProps = (store) => {
  return {
    field: store.field.selectedField,
    bulkPrescriptionFields: store.prescription.bulkPrescription.fields,
    bulkPrescriptionJobs: store.prescription.bulkPrescription.currentJobs,
    bulkFuturePrescriptionJobs: store.prescription.bulkPrescription.futureJobs,
  };
};

const PrescriptionSummary = ({
  classes,
  field,
  bulkPrescriptionFields,
  bulkPrescriptionJobs,
  bulkFuturePrescriptionJobs,
}) => {
  const LangFile = useLangFile();
  const { prescriptionJob } = usePrescriptionJob();
  const { fieldSize, totalPrescription, maxPrescription, unit } = prescriptionJob;
  const { showCurrentJob } = useMultipleJobs();
  const [shouldShowCurrentJob] = showCurrentJob;
  const { prescriptionState } = usePrescriptionJobOrchestration();

  const bulkPrescriptionFieldIndex = useMemo(() => {
    return bulkPrescriptionFields.findIndex(({ fieldId }) => fieldId === field.fieldId);
  }, [field, bulkPrescriptionFields]);

  const totalMaxPrescription = Object.values(bulkPrescriptionJobs || {}).reduce(
    (sum, job) => sum + (job.maxPrescription || 0),
    0
  );

  // Calculate the bulk total prescription, using future jobs if shouldShowCurrentJob is falsy
  const bulkTotalPrescription = (() => {
    const jobsSource = shouldShowCurrentJob ? bulkPrescriptionJobs : bulkFuturePrescriptionJobs;
    return Object.entries(jobsSource || {}).reduce((sum, [fieldId, job]) => {
      // Always use the current job's totalPrescription for the current field
      if (job.fieldId === prescriptionJob.fieldId) {
        return sum + (totalPrescription || 0);
      }
      return sum + (job.totalPrescription || 0);
    }, 0);
  })();

  const bulkTotalFieldSize = bulkPrescriptionFields.reduce((sum, f) => sum + (f.size || 0), 0);

  const showBulkSummary = prescriptionState === PrescriptionState.BULK_PRESCRIPTION_OVERVIEW;

  const summaryMaxPrescription = showBulkSummary ? totalMaxPrescription : maxPrescription;

  const summaryTotalPrescription = showBulkSummary ? bulkTotalPrescription : totalPrescription;

  const unitToPrescriptionTextValues = {
    [PRESCRIPTION_UNIT.LITER]: {
      totalPrescriptionText: `${Number(summaryTotalPrescription).toFixed(2)} ${LangFile.PrescriptionSettings.unit.liters.short}`,
      maxPrescriptionText: `${Number(summaryMaxPrescription).toFixed(2)} ${LangFile.PrescriptionSettings.unit.liters.short}`,
    },
    [PRESCRIPTION_UNIT.PIECES_M2]: {
      totalPrescriptionText: `${Number(summaryTotalPrescription / 1000).toFixed()} t.${LangFile.PrescriptionSettings.unit.pieces.short}`,
      maxPrescriptionText: `${Number(summaryMaxPrescription / 1000).toFixed(2)} t.${LangFile.PrescriptionSettings.unit.pieces.short}`,
    },
    [PRESCRIPTION_UNIT.KILOGRAM]: {
      totalPrescriptionText: `${Number(summaryTotalPrescription).toFixed(2)} ${LangFile.PrescriptionSettings.unit.kilogram.short}`,
      maxPrescriptionText: `${Number(summaryMaxPrescription).toFixed(2)} ${LangFile.PrescriptionSettings.unit.kilogram.short}`,
    },
  };

  const { totalPrescriptionText, maxPrescriptionText } = unitToPrescriptionTextValues[unit];

  const formatValue = (value, prescriptionUnit) => {
    if (prescriptionUnit === PRESCRIPTION_UNIT.PIECES_M2) {
      return `${Number(value / 1000).toFixed()} t.${LangFile.PrescriptionSettings.unit.pieces.short}`;
    }
    const unitLabel =
      prescriptionUnit === PRESCRIPTION_UNIT.LITER
        ? LangFile.PrescriptionSettings.unit.liters.short
        : LangFile.PrescriptionSettings.unit.kilogram.short;
    return `${Number(value).toFixed(2)} ${unitLabel}`;
  };

  const displayTotalPrescriptionText =
    prescriptionState === PrescriptionState.BULK_PRESCRIPTION_SINGLE_FIELD_VIEW
      ? `${formatValue(totalPrescription, unit)} ${LangFile.PrescriptionSummary.outOf} ${formatValue(bulkTotalPrescription, unit)}`
      : totalPrescriptionText;

  return (
    <div className={classes.root}>
      <Paper elevation={0} className={classes.items}>
        <div className={classes.item}>
          <span className={classes.label}>
            {showBulkSummary
              ? `${LangFile.PrescriptionSummary.numberOfFields}: ${bulkPrescriptionFields.length}`
              : `${LangFile.PrescriptionSummary.field}: ${getFieldName(field, LangFile)}`}
          </span>
          <span>-</span>
          <span className={classes.label}>
            {LangFile.PrescriptionSummary.arableLand}:{' '}
            {showBulkSummary ? Number(bulkTotalFieldSize.toFixed(2)) : Number(fieldSize.toFixed(2))}{' '}
            ha
          </span>
          {prescriptionState === PrescriptionState.BULK_PRESCRIPTION_SINGLE_FIELD_VIEW && (
            <span>{`(${bulkPrescriptionFieldIndex + 1}/${bulkPrescriptionFields.length})`}</span>
          )}
        </div>

        <div className={classes.item}>
          <span className={classes.label}>{LangFile.PrescriptionSummary.totalAmount}:</span>
          <span className={classes.label}>{displayTotalPrescriptionText}</span>
        </div>

        <div className={classes.item}>
          <span className={classes.label}>{LangFile.PrescriptionSummary.allocationMax}:</span>
          <span className={classes.label}>{maxPrescriptionText}</span>
        </div>
      </Paper>
    </div>
  );
};

export default memo(connect(mapStateToProps)(withStyles(styles)(PrescriptionSummary)));
